import { observer } from "mobx-react-lite";
import axios from "axios";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import ProtectRoute from "./auth/ProtectedRoute";
import { userStore } from "./store";
import QuestionAnswer from "./component/QuestionAnswer";

import Spinner from "./component/Spinner";
import { KunjiReleaseDates } from "./pages/KunjiReleaseDates";
import AddOrUpdateKunjiReleaseDate from "./pages/AddOrUpdateKunjiReleaseDate";
import AddPYQ from "./pages/AddPYQ";
import MtpAi from "./pages/ViewMtpAi";
import EditMtp from "./component/mtp/EditMtp";
const AddChapter = lazy(() => import("./pages/AddChapter"));
const AddNotes = lazy(() => import("./pages/AddNotes"));
const AddQuestion = lazy(() => import("./pages/AddQuestion"));
const AddTopic = lazy(() => import("./pages/AddTopic"));
const AddVideo = lazy(() => import("./pages/AddVideo"));
const AddWorksheet = lazy(() => import("./pages/AddWorksheet"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/Login"));
const ModifyNote = lazy(() => import("./pages/ModifyNote"));
const ModifyQuestion = lazy(() => import("./pages/ModifyQuestion"));
const Notes = lazy(() => import("./pages/Notes"));
const Questions = lazy(() => import("./pages/Questions"));
const Videos = lazy(() => import("./pages/Videos"));
const ViewPYQ = lazy(() => import("./pages/ViewPYQ"));
const WeeklyTests = lazy(() => import("./pages/WeeklyTests"));
const WeeklyTestDetail = lazy(() => import("./pages/WeeklyTestDetail"));
const WeeklyTestRound = lazy(() => import("./pages/WeeklyTestRound"));
const ViewAiPrediction = lazy(() => import("./pages/ViewAiPrediction"));
const Worksheets = lazy(() => import("./pages/Worksheets"));
const UpdateChapterAndTopic = lazy(() => import("./pages/UpdateC&T"));
const HandleMarks = lazy(() => import("./pages/HandleMarks"));
const AddMarks = lazy(() => import("./pages/AddMarks"));
const UploadImage = lazy(() => import("./pages/UploadImage"));
const ModifyWorksheet = lazy(() => import("./pages/ModifyWorksheet"));
const ViewImportantQuestions = lazy(() => import("./pages/ImportanQuestion"));
const AddImpQuestion = lazy(() => import("./pages/AddImpQuestion"));
const EditImpQuestion = lazy(() => import("./pages/EditImpQuestion"));
const UpdatePrediction = lazy(() =>
  import("./component/predictions/UpdatePrediction")
);

const App = observer(() => {
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Protected routes - accessible only if logged in */}
          <Route
            path="/"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <Home />
              </ProtectRoute>
            }
          />
          <Route
            path="/qna"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <QuestionAnswer />
              </ProtectRoute>
            }
          />
          <Route
            path="/addChapter"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddChapter />
              </ProtectRoute>
            }
          />
          <Route
            path="/addTopic"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddTopic />
              </ProtectRoute>
            }
          />
          
          <Route
            path="/addQuestion"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddQuestion />
              </ProtectRoute>
            }
          />
          <Route
            path="/weeklyTests"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <WeeklyTests />
              </ProtectRoute>
            }
          />
          <Route
            path="/weeklyTestRound"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <WeeklyTestRound />
              </ProtectRoute>
            }
          />
          <Route
            path="/weeklyTest/:id"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <WeeklyTestDetail />
              </ProtectRoute>
            }
          />
          <Route
            path="/modifyQuestion"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ModifyQuestion />
              </ProtectRoute>
            }
          />
          <Route
            path="/questions"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <Questions />
              </ProtectRoute>
            }
          />
          <Route
            path="/addVideo"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddVideo />
              </ProtectRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <Videos />
              </ProtectRoute>
            }
          />
          <Route
            path="/notes"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <Notes />
              </ProtectRoute>
            }
          />
          <Route
            path="/addNotes"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddNotes />
              </ProtectRoute>
            }
          />
          <Route
            path="/modifyNote"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ModifyNote />
              </ProtectRoute>
            }
          />
          <Route
            path="/worksheets"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <Worksheets />
              </ProtectRoute>
            }
          />
          <Route
            path="/addWorksheet"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddWorksheet />
              </ProtectRoute>
            }
          />
          <Route
            path="/modifyWorksheet"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ModifyWorksheet />
              </ProtectRoute>
            }
          />
          <Route
            path="/viewPYQ"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ViewPYQ />
              </ProtectRoute>
            }
          />
          <Route path="/addPYQ" element={ 
            <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddPYQ/>
              </ProtectRoute>
          }/>
          <Route
            path="/viewKunjiReleaseDate"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <KunjiReleaseDates />
              </ProtectRoute>
            }
          />
          <Route
            path="/viewMtpAi"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <MtpAi />
              </ProtectRoute>
            }
          />
          <Route
            path="/editMtp"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <EditMtp />
              </ProtectRoute>
            }
          />
          <Route
            path="/addOrUpdateKunjiReleaseDate"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddOrUpdateKunjiReleaseDate />
              </ProtectRoute>
            }
          />
          <Route
            path="/viewAiPrediction"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ViewAiPrediction />
              </ProtectRoute>
            }
          />
          <Route
            path="/updatePrediction"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <UpdatePrediction />
              </ProtectRoute>
            }
          />
          <Route
            path="/viewImportantQuestions"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <ViewImportantQuestions />
              </ProtectRoute>
            }
          />
          <Route
            path="/addImpQuestion"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddImpQuestion />
              </ProtectRoute>
            }
          />
          <Route
            path="/editImpQuestion"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <EditImpQuestion />
              </ProtectRoute>
            }
          />
          <Route
            path="/handleMarks"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <HandleMarks />
              </ProtectRoute>
            }
          />
          <Route
            path="/marks"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <AddMarks />
              </ProtectRoute>
            }
          />

          <Route
            path="/UpdateC&T"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <UpdateChapterAndTopic />
              </ProtectRoute>
            }
          />

          <Route
            path="/uploadImage"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <UploadImage />
              </ProtectRoute>
            }
          />
          <Route
            path="/update_prediction/:id"
            element={
              <ProtectRoute isAllowed={userStore.isLoggedIn} redirect="/login">
                <UpdatePrediction />
              </ProtectRoute>
            }
          />

          <Route
            path="/login"
            element={
              <ProtectRoute isAllowed={!userStore.isLoggedIn} redirect="/">
                <Login />
              </ProtectRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <ProtectRoute isAllowed={!userStore.isLoggedIn} redirect="/">
                <ForgotPassword />
              </ProtectRoute>
            }
          />
          <Route path="*" element={<div><h2>Page Not Found</h2></div>} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
});

export default App;
